import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { recipientsApi } from '@/lib/recipients/api';
import { useWorkspaceScopedEnabled } from '@/lib/api/use-workspace-scoped-enabled';
import { dataQualityKeys } from '@/hooks/api/query-keys';
import type { Recipient } from '@/lib/types/recipients';

// ============ Types ============

export interface DataQualityMetrics {
  overallScore: number;
  completenessScore: number;
  validityScore: number;
  duplicateScore: number;
  totalRecipients: number;
  incompleteProfiles: number;
  invalidEmails: number;
  duplicateCount: number;
}

export interface FieldCoverage {
  field: string;
  label: string;
  filledCount: number;
  totalCount: number;
  coveragePercent: number;
}

interface RawFieldCoverage {
  field: string;
  label?: string;
  filled?: number | null;
  total?: number | null;
  percentage?: number | null;
  filledCount?: number | null;
  totalCount?: number | null;
  coveragePercent?: number | null;
}

type FieldCoverageResponse = { fields?: RawFieldCoverage[] } | RawFieldCoverage[];

function normalizeFieldCoverage(raw: FieldCoverageResponse): FieldCoverage[] {
  const rows = Array.isArray(raw) ? raw : (raw.fields ?? []);
  return rows.map((row) => ({
    field: row.field,
    label: row.label ?? row.field,
    filledCount: row.filledCount ?? row.filled ?? 0,
    totalCount: row.totalCount ?? row.total ?? 0,
    coveragePercent: row.coveragePercent ?? row.percentage ?? 0,
  }));
}

// ============ Query Hooks ============

// Data-quality endpoints are workspace-scoped. The shared
// useWorkspaceScopedEnabled() hook handles the prod ('all'-aware) +
// demo (always-enabled) cases.

export function useDataQualityMetrics() {
  const enabled = useWorkspaceScopedEnabled();
  return useQuery({
    queryKey: dataQualityKeys.metrics(),
    queryFn: () => recipientsApi<DataQualityMetrics>('data-quality/metrics'),
    enabled,
  });
}

export function useIncompleteProfiles() {
  const enabled = useWorkspaceScopedEnabled();
  return useQuery({
    queryKey: dataQualityKeys.incompleteProfiles(),
    queryFn: async () => {
      const result = await fetchApi<{ data: Recipient[] } | Recipient[]>(
        '/recipients/data-quality/incomplete-profiles',
      );
      return Array.isArray(result) ? result : result.data;
    },
    enabled,
  });
}

export function useFieldCoverage() {
  const enabled = useWorkspaceScopedEnabled();
  return useQuery({
    queryKey: dataQualityKeys.fieldCoverage(),
    queryFn: () =>
      recipientsApi<FieldCoverageResponse>('data-quality/field-coverage'),
    select: normalizeFieldCoverage,
    enabled,
  });
}
